import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaEnvelopeOpenText, FaPen, FaTrash, FaPlus, FaTimes, FaInfoCircle, FaEye,
  FaInbox, FaUserCheck, FaClipboardList, FaComments, FaFlagCheckered,
} from "react-icons/fa";
import * as api from "../api/placementApi";
import {
  T, STAGE_OPTIONS, GlobalStyles, Spinner, PillSelect,
  overlayStyle, modalStyle, primaryBtnStyle, textBtnStyle, inputStyle, labelStyle,
} from "./uiKit";
import { buildEmailHtml, renderTemplateText } from "./emailHtml";

// Documents the {{placeholder}} tokens buildEmailTemplateVars() in
// PlacementAdminPage.js actually fills in — kept here (not imported) since
// this page has no reason to depend on the admin candidate-page module.
const PLACEHOLDERS = [
  { token: "{{name}}", desc: "Candidate's full name" },
  { token: "{{position}}", desc: "Position applied for" },
  { token: "{{org}}", desc: "Company name" },
  { token: "{{round}}", desc: "\" (Round Label)\" suffix, or blank if no round label" },
  { token: "{{roundLabelPhrase}}", desc: "\"**Round Label** interview\", or \"interview\" if none" },
  { token: "{{scheduled}}", desc: "Scheduled date/time, formatted" },
  { token: "{{venueLine}}", desc: "\"\\nVenue: address\" line, or blank if no company address set" },
  { token: "{{sign}}", desc: "Closing signature block (Kind regards + Talent Acquisition Team + company name)" },
];

// No real candidate exists yet while managing templates, so the live
// preview substitutes representative sample values for {{name}}/
// {{position}}/etc instead — company name/email/address are the one part
// pulled from the real Company Information, so the header/footer/{{org}}
// look exactly like what a candidate would actually receive.
function buildPreviewVars(companyName, companyAddress) {
  return {
    name: "John Doe",
    position: "Software Engineer",
    org: companyName || "Your Company",
    round: " (Technical Round 1)",
    roundLabelPhrase: "**Technical Round 1** interview",
    scheduled: "12 Aug 2026, 10:00 AM",
    venueLine: companyAddress ? `\nVenue: ${companyAddress}` : "\nVenue: 4th Floor, Business Park, Pune",
    sign: companyName
      ? `\n\nKind regards,\n**Talent Acquisition Team**\n**${companyName}**`
      : "\n\nKind regards,\n**Talent Acquisition Team**",
  };
}

const emptyForm = { key: "", label: "", stage: "ENQUIRY", subject: "", body: "" };

/* ── Add/Edit template modal ── key is only editable at creation — see
   the backend service comment: defaultTemplateKey() on the candidate page
   matches templates by key to auto-select the right default, so changing
   an existing key would silently break that. ── */
function TemplateModal({ template, initialStage, onClose, onDone, companyName, companyEmail, companyAddress }) {
  const isEdit = Boolean(template);
  const [form, setForm] = useState(template
    ? { key: template.key, label: template.label, stage: template.stage, subject: template.subject, body: template.body }
    : { ...emptyForm, stage: initialStage || emptyForm.stage });
  const [saving, setSaving] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSave = form.key.trim() && form.label.trim() && form.subject.trim() && form.body.trim();

  // Live preview — sample placeholder values stand in for a real candidate
  // (none exists yet while just managing templates), real company name/
  // email/address are used so header/footer/{{org}} match what a candidate
  // will actually see, and it recomputes on every keystroke.
  const previewVars = useMemo(() => buildPreviewVars(companyName, companyAddress), [companyName, companyAddress]);
  const htmlPreview = useMemo(
    () => buildEmailHtml({
      subject: renderTemplateText(form.subject, previewVars),
      body: renderTemplateText(form.body, previewVars),
      companyName,
      companyEmail,
      templateKey: form.key,
    }),
    [form.subject, form.body, form.key, previewVars, companyName, companyEmail]
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.updateEmailTemplate(template.id, {
          label: form.label, stage: form.stage, subject: form.subject, body: form.body,
        });
        toast.success("Template updated");
      } else {
        await api.createEmailTemplate(form);
        toast.success("Template added");
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @media (max-width: 760px) {
          .plc-tmpl-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={{ ...modalStyle, maxWidth: 980, width: "94%", maxHeight: "88vh", padding: 0, display: "flex", flexDirection: "column" }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>
            {isEdit ? "Edit Template" : "Add Template"}
          </span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div className="plc-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px" }}>
          <div className="plc-tmpl-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Key {isEdit && <span style={{ color: T.ink3, fontWeight: 400 }}>(locked)</span>}</label>
                  {isEdit ? (
                    <div style={{ ...inputStyle, background: "#f8fafc", color: T.ink3, fontFamily: T.fontMono, fontSize: 12.5 }}>{form.key}</div>
                  ) : (
                    <input
                      style={{ ...inputStyle, fontFamily: T.fontMono }}
                      value={form.key}
                      onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") }))}
                      placeholder="e.g. TEST_SCHEDULED"
                    />
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Stage</label>
                  <PillSelect value={form.stage} options={STAGE_OPTIONS} onChange={(v) => setForm((f) => ({ ...f, stage: v }))} placeholder="Select stage" />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Label</label>
                <input style={inputStyle} value={form.label} onChange={set("label")} placeholder="e.g. Test Scheduled" />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Subject</label>
                <input style={inputStyle} value={form.subject} onChange={set("subject")} placeholder="e.g. Test Scheduled — {{position}}" />
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>Body</label>
                <textarea rows={8} style={{ ...inputStyle, resize: "vertical", fontFamily: T.fontMono, fontSize: 12.5 }} value={form.body} onChange={set("body")} placeholder="Dear {{name}}, ..." />
              </div>

              <button
                type="button"
                onClick={() => setShowPlaceholders((s) => !s)}
                style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: T.primary, fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: "4px 0" }}
              >
                <FaInfoCircle size={12} /> {showPlaceholders ? "Hide" : "Show"} available placeholders
              </button>
              {showPlaceholders && (
                <div style={{ background: "#f8fafc", border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", marginTop: 10 }}>
                  {PLACEHOLDERS.map((p) => (
                    <div key={p.token} style={{ display: "flex", gap: 10, fontSize: 12, marginBottom: 6 }}>
                      <code style={{ fontFamily: T.fontMono, color: T.primaryDark, fontWeight: 700, flexShrink: 0, minWidth: 150 }}>{p.token}</code>
                      <span style={{ color: T.ink2 }}>{p.desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <label style={labelStyle}>Live Preview <span style={{ color: T.ink3, fontWeight: 400 }}>(sample data)</span></label>
              <div style={{ flex: 1, minHeight: 380, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: "#f4f6f8" }}>
                <iframe
                  title="Email preview"
                  srcDoc={htmlPreview}
                  style={{ width: "100%", height: "100%", minHeight: 380, border: "none", display: "block" }}
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 12,
          padding: "14px 24px", borderTop: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <button
            type="button"
            disabled={saving || !canSave}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving || !canSave ? 0.55 : 1, cursor: saving || !canSave ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ template, onClose, onConfirm, deleting }) {
  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 400 }}>
        <div style={{ fontFamily: T.fontDisplay, fontSize: 16, fontWeight: 700, color: T.ink1, marginBottom: 8 }}>Delete template?</div>
        <div style={{ fontSize: 13, color: T.ink2, marginBottom: 20 }}>
          "{template.label}" ({template.key}) will no longer be selectable when sending emails. This can't be undone.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button
            type="button"
            disabled={deleting}
            onClick={onConfirm}
            style={{ ...primaryBtnStyle, background: T.danger, opacity: deleting ? 0.55 : 1, cursor: deleting ? "not-allowed" : "pointer" }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Same accent palette as StagePill in uiKit.js (kept in sync deliberately —
// that map isn't exported) so a stage reads as the same color everywhere
// in the Recruitment module, not just on this page.
const STAGE_META = {
  ENQUIRY:   { label: "Enquiry",   icon: FaInbox,          bg: T.amberSoft,   fg: "#b45309" },
  SCREENING: { label: "Screening", icon: FaUserCheck,      bg: T.tealSoft,    fg: T.teal },
  TEST:      { label: "Test",      icon: FaClipboardList,  bg: T.cyanSoft,    fg: T.cyan },
  INTERVIEW: { label: "Interview", icon: FaComments,       bg: T.purpleSoft,  fg: T.purple },
  RESULT:    { label: "Result",    icon: FaFlagCheckered,  bg: T.primarySoft, fg: T.primaryDark },
};
const STAGE_LABELS = Object.fromEntries(Object.entries(STAGE_META).map(([k, v]) => [k, v.label]));

/* ── View template modal ── read-only: full subject/body text (not the
   truncated table preview), opened from the eye icon or clicking anywhere
   on the row — an Edit button here hands off to the same TemplateModal the
   pencil icon opens, rather than duplicating editable fields in this
   read-only view. ── */
function ViewTemplateModal({ template, onClose, onEdit, companyName, companyEmail, companyAddress }) {
  // Same sample-data substitution as the Add/Edit preview — renders the
  // actual branded email a candidate would receive, not the raw
  // {{placeholder}} text stored on the template.
  const htmlPreview = useMemo(() => {
    const vars = buildPreviewVars(companyName, companyAddress);
    return buildEmailHtml({
      subject: renderTemplateText(template.subject, vars),
      body: renderTemplateText(template.body, vars),
      companyName,
      companyEmail,
      templateKey: template.key,
    });
  }, [template, companyName, companyEmail, companyAddress]);

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 640, maxHeight: "88vh", overflowY: "auto" }} className="plc-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>{template.label}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <button type="button" onClick={onEdit} title="Edit" style={{ border: "none", background: "transparent", color: T.ink2, cursor: "pointer", fontSize: 13, padding: 8 }}>
              <FaPen size={13} />
            </button>
            <button type="button" onClick={onClose} title="Close" style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
              <FaTimes />
            </button>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
          <code style={{
            fontSize: 10.5, fontFamily: T.fontMono, fontWeight: 600, borderRadius: 20, padding: "2px 8px",
            color: (STAGE_META[template.stage] || {}).fg || T.ink3,
            background: (STAGE_META[template.stage] || {}).bg || "#f1f5f9",
          }}>{template.key}</code>
          <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>{STAGE_LABELS[template.stage] || template.stage}</span>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Subject</label>
          <div style={{ fontSize: 13.5, color: T.ink1 }}>{template.subject}</div>
        </div>

        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Email Preview <span style={{ color: T.ink3, fontWeight: 400 }}>(sample data)</span></label>
          <div style={{ height: 430, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: "#f4f6f8" }}>
            <iframe
              title="Email preview"
              srcDoc={htmlPreview}
              style={{ width: "100%", height: "100%", border: "none", display: "block" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Close</button>
        </div>
      </div>
    </div>
  );
}

const theadCellStyle = {
  textAlign: "left", padding: "12px 16px", fontSize: 11.5, fontWeight: 700, color: T.ink3,
  textTransform: "uppercase", letterSpacing: ".4px", fontFamily: T.fontDisplay,
  position: "sticky", top: 0, background: "#fafbfc", zIndex: 1, boxShadow: `0 1px 0 ${T.border}`,
};

// Circular icon-only action button — hover fills in with `bg` — shared by
// the table's View/Edit/Delete actions for a more tactile, clickable feel
// than a flat transparent icon.
function RowIconBtn({ onClick, title, color, bg, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 28, height: 28, borderRadius: 8, border: "none", background: "transparent",
        color, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, transition: "background .12s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = bg)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}
const tdCellStyle = {
  padding: "13px 16px", fontSize: 13, color: T.ink2, verticalAlign: "middle",
};

// Navbar-agnostic content — Admin/Manager/Employee wrapper pages each embed
// this with their own navbar, same pattern as PlacementAdminPage/MyInterviewsPage.
export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState("ENQUIRY");
  const [viewTarget, setViewTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null); // template object, or "NEW"
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");

  useEffect(() => {
    api.getCompanyName()
      .then((data) => {
        setCompanyName(data?.companyName || "");
        setCompanyEmail(data?.email || "");
        setCompanyAddress(data?.address || "");
      })
      .catch(() => {}); // preview just falls back to "Your Company" / no venue line
  }, []);

  const load = () => {
    setLoading(true);
    api.getEmailTemplates()
      .then((list) => setTemplates(list || []))
      .catch(() => toast.error("Failed to load email templates"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const byStage = {};
    STAGE_OPTIONS.forEach((s) => { byStage[s.value] = []; });
    templates.forEach((t) => {
      if (!byStage[t.stage]) byStage[t.stage] = [];
      byStage[t.stage].push(t);
    });
    return byStage;
  }, [templates]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteEmailTemplate(deleteTarget.id);
      toast.success("Template deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.message || "Failed to delete template");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bg }}>
      <GlobalStyles />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, padding: "28px 32px 0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 11, flexShrink: 0,
              background: "linear-gradient(135deg,#818cf8,#4f46e5)",
              boxShadow: "0 4px 12px rgba(79,70,229,0.28)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FaEnvelopeOpenText size={15} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 21, color: T.ink1, fontWeight: 700, fontFamily: T.fontDisplay, letterSpacing: "-.2px" }}>Email Templates</h2>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>Manage the wording used across every stage's Send Email screen</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEditTarget("NEW")}
            title="Add a template for the currently active stage tab"
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 9,
              border: "none", background: T.primary, color: "#fff", fontWeight: 600, fontSize: 13.5,
              cursor: "pointer", boxShadow: "0 4px 12px rgba(99,102,241,0.32)", transition: "transform .12s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            <FaPlus size={12} /> Add Template
          </button>
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 22, borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          {STAGE_OPTIONS.map((s) => {
            const meta = STAGE_META[s.value];
            const Icon = meta.icon;
            const count = (grouped[s.value] || []).length;
            const active = activeStage === s.value;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setActiveStage(s.value)}
                className="plc-btn"
                style={{
                  border: "none", cursor: "pointer",
                  padding: "9px 14px", marginBottom: -1, fontFamily: T.fontBody, borderRadius: "9px 9px 0 0",
                  fontSize: 13.5, fontWeight: 700, color: active ? meta.fg : T.ink3,
                  background: active ? meta.bg : "transparent",
                  borderBottom: active ? `2px solid ${meta.fg}` : "2px solid transparent",
                  display: "flex", alignItems: "center", gap: 8, transition: "background .12s ease, color .12s ease",
                }}
              >
                <Icon size={12} />
                {meta.label}
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20,
                  background: active ? "rgba(255,255,255,0.65)" : "#f1f5f9", color: active ? meta.fg : T.ink3,
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <Spinner padding="80px 0" />
        ) : (grouped[activeStage] || []).length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: T.ink3 }}>
            <div style={{
              width: 52, height: 52, borderRadius: "50%", margin: "0 auto 14px",
              background: STAGE_META[activeStage].bg, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FaEnvelopeOpenText size={20} color={STAGE_META[activeStage].fg} />
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink2 }}>No {STAGE_LABELS[activeStage].toLowerCase()} templates yet</div>
            <div style={{ fontSize: 12.5, marginTop: 4, marginBottom: 18 }}>Add one to make it selectable from the Send Email screen.</div>
            <button
              type="button"
              onClick={() => setEditTarget("NEW")}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 9, border: "none", background: T.primary, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
            >
              <FaPlus size={11} /> Add {STAGE_LABELS[activeStage]} Template
            </button>
          </div>
        ) : (
          <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "hidden", marginBottom: 28, display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            <div className="plc-scroll" style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...theadCellStyle, width: 48 }}>Sr.No</th>
                    <th style={theadCellStyle}>Label</th>
                    <th style={theadCellStyle}>Key</th>
                    <th style={theadCellStyle}>Subject</th>
                    <th style={theadCellStyle}>Body</th>
                    <th style={{ ...theadCellStyle, width: 112 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(grouped[activeStage] || []).map((t, idx) => {
                    const meta = STAGE_META[t.stage] || { bg: "#f1f5f9", fg: T.ink3 };
                    return (
                      <tr key={t.id} onClick={() => setViewTarget(t)} className="plc-row" style={{ borderTop: `1px solid ${T.border}`, cursor: "pointer" }}>
                        <td style={{ ...tdCellStyle, color: T.ink3, fontFamily: T.fontMono, fontSize: 12 }}>{idx + 1}</td>
                        <td style={{ ...tdCellStyle, fontWeight: 600, color: T.ink1, whiteSpace: "nowrap" }}>{t.label}</td>
                        <td style={tdCellStyle}>
                          <code style={{ fontSize: 10.5, fontFamily: T.fontMono, fontWeight: 600, color: meta.fg, background: meta.bg, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>{t.key}</code>
                        </td>
                        <td style={{ ...tdCellStyle, maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.subject}</td>
                        <td style={{ ...tdCellStyle, maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: T.ink3, fontStyle: "italic" }}>
                          {t.body?.replace(/\n+/g, " ")}
                        </td>
                        <td style={tdCellStyle}>
                          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <RowIconBtn onClick={() => setViewTarget(t)} title="View" color="#2563eb" bg="#eff6ff">
                              <FaEye size={12} />
                            </RowIconBtn>
                            <RowIconBtn onClick={(e) => { e.stopPropagation(); setEditTarget(t); }} title="Edit" color={T.ink2} bg="#f1f5f9">
                              <FaPen size={12} />
                            </RowIconBtn>
                            <RowIconBtn onClick={(e) => { e.stopPropagation(); setDeleteTarget(t); }} title="Delete" color={T.danger} bg={T.dangerSoft}>
                              <FaTrash size={12} />
                            </RowIconBtn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {viewTarget && (
        <ViewTemplateModal
          template={viewTarget}
          onClose={() => setViewTarget(null)}
          onEdit={() => { setEditTarget(viewTarget); setViewTarget(null); }}
          companyName={companyName}
          companyEmail={companyEmail}
          companyAddress={companyAddress}
        />
      )}
      {editTarget && (
        <TemplateModal
          template={editTarget === "NEW" ? null : editTarget}
          initialStage={activeStage}
          onClose={() => setEditTarget(null)}
          onDone={load}
          companyName={companyName}
          companyEmail={companyEmail}
          companyAddress={companyAddress}
        />
      )}
      {deleteTarget && (
        <ConfirmDeleteModal
          template={deleteTarget}
          deleting={deleting}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
